import React, { useEffect, useState } from 'react';
import { DashboardLayout } from '../../layouts/DashboardLayout';
import { getBusinessTypes, createBusinessType, updateBusinessType, deleteBusinessType, seedBusinessTypes } from '../../services/api';
import { Building2, Plus, Edit, Trash2, X, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { getErrorMessage } from '../../utils/errorUtils';

const TiposNegocio = () => {
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingType, setEditingType] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    description: '',
    icon: '',
    order: 0,
    is_active: true
  });

  useEffect(() => {
    loadTypes();
  }, []);

  const loadTypes = async () => {
    try {
      const response = await getBusinessTypes({ active_only: false });
      setTypes(response.data);
    } catch (error) {
      console.error('Error loading business types:', error);
      toast.error('Error al cargar tipos de negocio');
    } finally {
      setLoading(false);
    }
  };

  const handleSeed = async () => {
    try {
      const response = await seedBusinessTypes();
      toast.success(response.data.message);
      loadTypes();
    } catch (error) {
      console.error('Error seeding business types:', error);
      toast.error('Error al crear tipos de negocio');
    }
  };

  const generateSlug = (name) => {
    return name
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  };

  const handleNameChange = (name) => {
    const newFormData = { ...formData, name };
    if (!editingType) {
      newFormData.slug = generateSlug(name);
    }
    setFormData(newFormData);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      if (editingType) {
        await updateBusinessType(editingType.id, formData);
        toast.success('Tipo de negocio actualizado');
      } else {
        await createBusinessType(formData);
        toast.success('Tipo de negocio creado');
      }
      setShowModal(false);
      resetForm();
      loadTypes();
    } catch (error) {
      console.error('Error saving business type:', error);
      toast.error(getErrorMessage(error, 'Error al guardar tipo de negocio'));
    }
  };

  const handleEdit = (type) => {
    setEditingType(type);
    setFormData({
      name: type.name,
      slug: type.slug,
      description: type.description || '',
      icon: type.icon || '',
      order: type.order || 0,
      is_active: type.is_active
    });
    setShowModal(true);
  };

  const handleDelete = async (type) => {
    if (!window.confirm(`¿Estás seguro de eliminar "${type.name}"?`)) return;

    try {
      await deleteBusinessType(type.id);
      toast.success('Tipo de negocio eliminado');
      loadTypes();
    } catch (error) {
      console.error('Error deleting business type:', error);
      toast.error(getErrorMessage(error, 'Error al eliminar tipo de negocio'));
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      slug: '',
      description: '',
      icon: '',
      order: 0,
      is_active: true
    });
    setEditingType(null);
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 tracking-tight">Tipos de Negocio</h1>
          <p className="text-slate-500 mt-1">Administra los tipos de negocio disponibles para registro y marketplace</p>
        </div>

        {/* Actions */}
        <div className="flex justify-between items-center">
          <p className="text-slate-600">{types.length} tipos registrados</p>
          <div className="flex gap-3">
            {types.length === 0 && (
              <button
                onClick={handleSeed}
                className="inline-flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 font-medium transition-colors"
              >
                <Sparkles className="w-4 h-4" />
                Crear Tipos Iniciales
              </button>
            )}
            <button
              onClick={() => {
                resetForm();
                setShowModal(true);
              }}
              className="inline-flex items-center gap-2 px-4 py-2 bg-teal-700 text-white rounded-lg hover:bg-teal-800 font-medium transition-colors"
            >
              <Plus className="w-4 h-4" />
              Nuevo Tipo
            </button>
          </div>
        </div>

        {/* Types Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {types.map((type) => (
            <div
              key={type.id}
              className={`bg-white rounded-xl border ${
                type.is_active ? 'border-slate-200' : 'border-red-200 bg-red-50'
              } p-4 hover:shadow-md transition-shadow`}
            >
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <span className="text-2xl">{type.icon || '🏢'}</span>
                  <div>
                    <h3 className="font-semibold text-slate-900">{type.name}</h3>
                    <p className="text-xs text-slate-400 font-mono">{type.slug}</p>
                    {type.description && (
                      <p className="text-sm text-slate-500 line-clamp-1 mt-0.5">{type.description}</p>
                    )}
                  </div>
                </div>
                <div className="flex gap-1">
                  <button
                    onClick={() => handleEdit(type)}
                    className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                  >
                    <Edit className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(type)}
                    className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
              <div className="flex items-center justify-between mt-3 pt-3 border-t border-slate-100">
                <span className="text-xs text-slate-400">Orden: {type.order}</span>
                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                  type.is_active
                    ? 'bg-green-100 text-green-700'
                    : 'bg-red-100 text-red-700'
                }`}>
                  {type.is_active ? 'Activo' : 'Inactivo'}
                </span>
              </div>
            </div>
          ))}
        </div>

        {types.length === 0 && (
          <div className="bg-white rounded-xl p-12 text-center border border-slate-200">
            <Building2 className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-slate-700">No hay tipos de negocio</h3>
            <p className="text-slate-500 mt-1">Crea tipos de negocio para que aparezcan en el registro y marketplace</p>
            <button
              onClick={handleSeed}
              className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 font-medium transition-colors"
            >
              <Sparkles className="w-4 h-4" />
              Crear Tipos Iniciales
            </button>
          </div>
        )}

        {/* Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
              <div className="flex items-center justify-between p-6 border-b border-slate-200">
                <h2 className="text-2xl font-bold text-slate-900">
                  {editingType ? 'Editar Tipo' : 'Nuevo Tipo de Negocio'}
                </h2>
                <button
                  onClick={() => {
                    setShowModal(false);
                    resetForm();
                  }}
                  className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 text-slate-500" />
                </button>
              </div>
              <form onSubmit={handleSubmit} className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Nombre *</label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => handleNameChange(e.target.value)}
                    placeholder="Ej: Restaurantes"
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Slug *</label>
                  <input
                    type="text"
                    required
                    value={formData.slug}
                    onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                    placeholder="restaurantes"
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500 font-mono text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Descripción</label>
                  <input
                    type="text"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Ej: Restaurantes, cafeterías, fondas"
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Icono (emoji)</label>
                    <input
                      type="text"
                      value={formData.icon}
                      onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
                      placeholder="🍽️"
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Orden</label>
                    <input
                      type="number"
                      min="0"
                      value={formData.order}
                      onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) || 0 })}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500"
                    />
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="is_active"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                    className="rounded border-slate-300 text-teal-600 focus:ring-teal-500"
                  />
                  <label htmlFor="is_active" className="text-sm text-slate-700">Tipo activo</label>
                </div>
                <div className="flex gap-3 pt-4">
                  <button
                    type="submit"
                    className="flex-1 px-4 py-2 bg-teal-700 text-white rounded-lg hover:bg-teal-800 font-medium transition-colors"
                  >
                    {editingType ? 'Actualizar' : 'Crear'}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowModal(false);
                      resetForm();
                    }}
                    className="px-4 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 font-medium transition-colors"
                  >
                    Cancelar
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default TiposNegocio;
